import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Flame, Sparkles, Camera, Eye } from 'lucide-react';
import { useParallax } from "@/hooks/useParallax";
import { useAppConfig } from "@/hooks/useAppConfig";
import StreamText from "./effects/StreamText";

interface PhotoCardsProps {
  t: Record<string, string>;
  captions: string[];
  active: boolean;
}

const DEFAULT_PHOTOS = ["/photos/01.png", "/photos/02.png", "/photos/03.png", "/photos/04.png"];
const ease = [0.2, 0.8, 0.2, 1] as const;

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
  exit: { transition: { staggerChildren: 0.04, staggerDirection: -1 } },
};
const fadeSlideUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease } },
  exit: { opacity: 0, y: -10, transition: { duration: 0.25 } },
};
const scaleReveal = {
  hidden: { opacity: 0, scale: 0.94, y: 16 },
  visible: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.65, ease, delay: 0.15 } },
  exit: { opacity: 0, scale: 0.96, transition: { duration: 0.3 } },
};
const thumbVariant = {
  hidden: { opacity: 0, x: 12 },
  visible: (i: number) => ({
    opacity: 1, x: 0,
    transition: { delay: 0.25 + i * 0.08, duration: 0.5, ease },
  }),
  exit: { opacity: 0, x: 8, transition: { duration: 0.2 } },
};

export const PhotoCards: React.FC<PhotoCardsProps> = ({ t, captions, active }) => {
  const { config } = useAppConfig();
  const photos = (config.photos && config.photos.length > 0) ? config.photos : DEFAULT_PHOTOS;
  const [idx, setIdx] = useState(0);
  const total = photos.length;
  const parallax = useParallax(10, active);
  const streamKey = useRef(0);

  useEffect(() => {
    if (active) { setIdx(0); streamKey.current++; }
  }, [active]);

  useEffect(() => {
    const img = new Image();
    img.src = photos[(idx + 1) % total];
  }, [idx, total]);

  useEffect(() => {
    if (!active) return;
    const timer = setTimeout(() => {
      setIdx(prev => (prev + 1) % total);
    }, 5000);
    return () => clearTimeout(timer);
  }, [idx, active, total]);

  const caption = captions[idx] || t[`photo_caption_${idx+1}`] || t.gallery_fallback_caption || "Nuevo contenido.";

  return (
    <div className="absolute inset-0" style={{ color: 'var(--c-text)', fontFamily: 'var(--ff-body)' }}>
      <AnimatePresence mode="wait">
        {active && (
          <motion.div
            key="gallery-content"
            variants={stagger}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="absolute inset-0"
          >
            {/* Top title area */}
            <motion.div
              variants={fadeSlideUp}
              style={{
                position: 'absolute', top: 'var(--safe-top, 62px)',
                left: 18, right: 18, paddingTop: 'var(--sp-4)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
              }}
            >
              <div>
                <div style={{
                  fontFamily: 'var(--ff-body)', fontSize: 'var(--fs-micro)',
                  letterSpacing: '0.22em', textTransform: 'uppercase',
                  color: 'var(--c-crimson)', fontWeight: 600,
                }}>
                  <StreamText key={`gal-eb-${streamKey.current}`} text={t.gallery_title || "Galería"} speed={40} delay={100} caret={false} />
                </div>
                <h2 style={{
                  fontFamily: 'var(--ff-display)', fontSize: 'var(--fs-subtitle)',
                  margin: '6px 0 0 0', lineHeight: 0.9, textTransform: 'uppercase',
                }}>{t.gallery_subtitle || "Vista previa"}</h2>
              </div>
              <div style={{ fontFamily: 'var(--ff-display)', fontSize: 22, letterSpacing: '0.05em' }}>
                {String(idx + 1).padStart(2, '0')}
                <span style={{ color: 'var(--c-muted)' }}>/0{total}</span>
              </div>
            </motion.div>

            {/* Big visual slot with parallax */}
            <motion.div variants={scaleReveal}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={`shot-${idx}`}
                  initial={{ opacity: 0, scale: 0.97, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.97, y: -8 }}
                  transition={{ duration: 0.45, ease }}
                  style={{
                    position: 'absolute', top: 'calc(var(--safe-top, 62px) + 100px)',
                    left: 18, right: 60,
                    height: 420, border: '1px solid var(--c-line)', overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: '100%', height: '100%',
                      transform: `translate3d(${parallax.x}px, ${parallax.y}px, 0)`,
                      transition: 'transform 0.1s linear',
                    }}
                  >
                    <img
                      src={photos[idx]}
                      alt={caption}
                      className="animate-ken-burns"
                      style={{
                        width: '100%', height: '100%', objectFit: 'cover', display: 'block',
                      }}
                    />
                  </div>
                  <div style={{
                    position: 'absolute', left: 0, right: 0, bottom: 0,
                    padding: '60px 18px 18px',
                    background: 'linear-gradient(to top, var(--c-bg) 0%, color-mix(in srgb, var(--c-bg) 40%, transparent) 60%, transparent 100%)',
                  }}>
                    <div style={{
                      fontFamily: 'var(--ff-body)', fontSize: 'var(--fs-micro)',
                      letterSpacing: '0.22em', textTransform: 'uppercase',
                      color: 'var(--c-crimson)', fontWeight: 600,
                    }}>{t.gallery_tag || "Lo nuevo"}</div>
                    <p style={{
                      margin: '6px 0 0 0', fontFamily: 'var(--ff-italic)', fontStyle: 'italic',
                      fontSize: 22, lineHeight: 1.15, maxWidth: 220, color: 'var(--c-text)',
                      display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'
                    }}>
                      <span>"<StreamText key={`cap-${idx}-${streamKey.current}`} text={caption} speed={35} delay={300} caret={false} />"</span>
                      {[
                        <Flame key="icon-flame" style={{ width: 18, height: 18, color: 'var(--c-crimson)', flexShrink: 0 }} />,
                        <Sparkles key="icon-sparkles" style={{ width: 18, height: 18, color: 'var(--c-crimson)', flexShrink: 0 }} />,
                        <Camera key="icon-camera" style={{ width: 18, height: 18, color: 'var(--c-crimson)', flexShrink: 0 }} />,
                        <Eye key="icon-eye" style={{ width: 18, height: 18, color: 'var(--c-crimson)', flexShrink: 0 }} />
                      ][idx]}
                    </p>
                  </div>

                  <motion.div
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 5, ease: 'linear' }}
                    style={{
                      position: 'absolute', left: 0, top: 0, height: 2,
                      background: 'var(--c-crimson)', width: '100%', transformOrigin: '0 50%',
                    }}
                  />
                </motion.div>
              </AnimatePresence>
            </motion.div>

            {/* Thumb strip */}
            <div style={{
              position: 'absolute', top: 'calc(var(--safe-top, 62px) + 100px)',
              right: 18, width: 34, display: 'flex', flexDirection: 'column', gap: 8,
            }}>
              {photos.map((p, n) => (
                <motion.button
                  key={n}
                  variants={thumbVariant}
                  custom={n}
                  whileHover={{ scale: 1.08, opacity: 1 }}
                  whileTap={{ scale: 0.94 }}
                  onClick={() => setIdx(n)}
                  aria-label={`Foto ${n + 1}`}
                  style={{
                    padding: 0, background: 'transparent', cursor: 'pointer',
                    width: 34, height: 98,
                    border: `1px solid ${n === idx ? 'var(--c-crimson)' : 'var(--c-line)'}`,
                    opacity: n === idx ? 1 : 0.55, position: 'relative',
                    transition: 'border-color .3s',
                  }}
                >
                  <img src={p} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  <div style={{
                    position: 'absolute', top: 4, left: 4,
                    fontSize: 9, color: 'var(--c-text)', fontWeight: 600,
                    fontFamily: 'var(--ff-display)', letterSpacing: '0.05em',
                    textShadow: '0 1px 4px rgba(0,0,0,.8)',
                  }}>0{n + 1}</div>
                </motion.button>
              ))}
            </div>

            {/* Controls + Footer */}
            <motion.div
              variants={fadeSlideUp}
              style={{
                position: 'absolute', bottom: 90, left: 18, right: 18,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}
            >
              <div style={{
                display: 'flex', alignItems: 'baseline', gap: 10,
                fontFamily: 'var(--ff-body)', color: 'var(--c-text-dim)',
                fontSize: 'var(--fs-caption)', letterSpacing: '0.18em', textTransform: 'uppercase',
              }}>
                <span style={{
                  fontFamily: 'var(--ff-display)', fontSize: 22,
                  color: 'var(--c-text)', letterSpacing: '0.02em', lineHeight: 1,
                }}>02</span>
                <span style={{ color: 'var(--c-muted)' }}>/ 05</span>
                <span style={{ width: 24, height: 1, background: 'var(--c-line)', margin: '0 6px', display: 'inline-block' }}></span>
                <span>{t.gallery_title || "Galería"}</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <motion.button
                  whileHover={{ scale: 1.1, borderColor: 'var(--c-text-dim)' }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setIdx((idx - 1 + total) % total)}
                  aria-label="Foto anterior"
                  style={{
                    width: 36, height: 36, borderRadius: '50%',
                    border: '1px solid var(--c-line)', background: 'transparent',
                    color: 'var(--c-text-dim)', fontSize: 14, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <ChevronLeft size={16} />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setIdx((idx + 1) % total)}
                  aria-label="Siguiente foto"
                  style={{
                    width: 36, height: 36, borderRadius: '50%',
                    border: '1px solid var(--c-crimson)', background: 'var(--c-crimson)',
                    color: 'var(--c-on-crimson)', fontSize: 14, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 0 10px rgb(var(--c-crimson-rgb) / 0.3)',
                  }}
                >
                  <ChevronRight size={16} />
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PhotoCards;
